import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import ReactDOM from 'react-dom';
import classNames from 'classnames';
import { Button } from 'reactstrap';
import { faGripVertical } from '@fortawesome/pro-solid-svg-icons';
import {
  draggable,
  dropTargetForElements,
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import { setCustomNativeDragPreview } from '@atlaskit/pragmatic-drag-and-drop/element/set-custom-native-drag-preview';
import {
  attachClosestEdge,
  extractClosestEdge,
} from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';

import { Icon } from 'js/components';
import { useEditorContext } from 'js/ydoc/docs/capabilities-assessment/EditorContext';
import { useCollaboratorForField } from 'js/ydoc/doc/hooks';
import { EditorInput } from 'js/components/Assessment/Editors/common/components/EditorInput';
import DropIndicator from 'js/components/Assessment/Editors/common/components/DropIndicator';
import ErrorMessage from '../../../common/components/EditorErrors/ErrorMessage';
import PageActions from '../PageActions';
import PageTitleError from '../PageTitleError';
import PageHeaderToggle from '../PageHeaderToggle';
import { shouldRenderPageHeader } from 'js/engines/CapabilitiesAssessment/utils';
import Element from '../Element';

import styles from '../../CapabilitiesAssessmentEditor.module.scss';

/** Standard questions only drag among standard pages. */
export const STANDARD_QUESTION_DRAG_GROUP = 'standard-questions';

function DragPreview({ page, width }) {
  return (
    <div
      className={classNames(styles.page, styles['page-standard'])}
      style={{ width }}
    >
      <div className={styles['page-content']}>
        <div className={styles['page-header']}>
          <div className={styles['drag-handle']}>
            <button className="drag-handle">
              <Icon icon={faGripVertical} className="icon" />
            </button>
          </div>
          <div className={styles['page-guid']}>{page.guid}</div>
        </div>
        <div className={styles['page-title']}>{page.title}</div>
        <div className={styles['page-description']}>{page.description}</div>
      </div>
    </div>
  );
}

function StandardElement({ page, element, index }) {
  const { copyStandardElement, deleteStandardElement } = useEditorContext();

  const handleCopy = useCallback(() => {
    copyStandardElement(element.guid);
  }, [copyStandardElement, element.guid]);

  const handleDelete = useCallback(() => {
    deleteStandardElement(element.guid);
  }, [deleteStandardElement, element.guid]);

  return (
    <Element
      page={page}
      element={element}
      index={index}
      dragGroup={STANDARD_QUESTION_DRAG_GROUP}
      onCopy={handleCopy}
      onDelete={handleDelete}
    />
  );
}

function StandardQuestionsPage({ page }) {
  const {
    updatePageFields,
    movePageAfter,
    movePageBefore,
    addElementToStandardPage,
  } = useEditorContext();

  const collaboratorForTitle = useCollaboratorForField(
    'page',
    page.guid,
    'title'
  );

  const collaboratorForDescription = useCollaboratorForField(
    'page',
    page.guid,
    'description'
  );

  const headerHidden = !shouldRenderPageHeader(page);

  const handleFieldChange = useCallback(
    ({ target: { name, value } }) => {
      updatePageFields(page.guid, { [name]: value });
    },
    [page.guid, updatePageFields]
  );

  const handleAddQuestion = useCallback(() => {
    addElementToStandardPage(page.guid);
  }, [page.guid, addElementToStandardPage]);

  const elements = useMemo(() => page.elements ?? [], [page.elements]);
  const ref = useRef(null);
  const dragHandleRef = useRef(null);
  const [isDraggedOver, setIsDraggedOver] = useState(false);
  const [isDropTarget, setIsDropTarget] = useState(false);
  const [closestEdge, setClosestEdge] = useState(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    return combine(
      draggable({
        element: ref.current,
        dragHandle: dragHandleRef.current,
        getInitialData: () => ({ page }),
        onGenerateDragPreview: ({ nativeSetDragImage }) => {
          setCustomNativeDragPreview({
            render({ container }) {
              ReactDOM.render(
                <DragPreview page={page} width={ref.current.scrollWidth} />,
                container
              );
              return function cleanup() {
                ReactDOM.unmountComponentAtNode(container);
              };
            },
            getOffset: () => ({ x: 20, y: 16 }),
            nativeSetDragImage,
          });
        },
      }),
      dropTargetForElements({
        element: ref.current,
        getData: (args) => {
          return attachClosestEdge(
            { page },
            {
              input: args.input,
              element: args.element,
              allowedEdges: ['top', 'bottom'],
            }
          );
        },
        // Pages reorder here; standard questions dropped on the page itself
        // (not on another question) are moved by the editor's monitor
        canDrop: ({ source }) =>
          (!!source.data.page && source.data.page.guid !== page.guid) ||
          (!!source.data.question &&
            source.data.dragGroup === STANDARD_QUESTION_DRAG_GROUP),
        onDragEnter: ({ source }) => {
          setIsDraggedOver(true);
          setIsDropTarget(
            !!source.data.question &&
              !elements.some((e) => e.guid === source.data.question.guid)
          );
        },
        onDrag({ self, source }) {
          const isSource = source.element === el;
          if (isSource || !source.data.page) {
            setClosestEdge(null);
            return;
          }

          setClosestEdge(extractClosestEdge(self.data));
        },
        onDragLeave: () => {
          setIsDraggedOver(false);
          setIsDropTarget(false);
        },
        onDrop: ({ source, location }) => {
          setIsDraggedOver(false);
          setIsDropTarget(false);
          setClosestEdge(null);

          if (location.current.dropTargets.length > 1 || !source.data.page) {
            // A question is the target, or a question is being dropped
            return;
          }

          if (source.data.page.guid !== page.guid) {
            if (closestEdge === 'top') {
              movePageBefore(source.data.page.guid, page.guid);
            } else if (closestEdge === 'bottom') {
              movePageAfter(source.data.page.guid, page.guid);
            }
          }
        },
      })
    );
  }, [page, elements, movePageAfter, movePageBefore, closestEdge]);

  const rootClassName = useMemo(() => {
    return classNames(styles.page, styles['page-standard'], {
      'dragged-over': isDraggedOver,
      'drop-target': isDropTarget,
    });
  }, [isDraggedOver, isDropTarget]);

  return (
    <div className={rootClassName} ref={ref}>
      {isDraggedOver && closestEdge !== null && (
        <DropIndicator
          top={closestEdge === 'top'}
          bottom={closestEdge === 'bottom'}
        />
      )}
      <div className={styles['page-content']}>
        <div className={styles['page-header']}>
          <div className={styles['drag-handle']} ref={dragHandleRef}>
            <button className="drag-handle">
              <Icon icon={faGripVertical} className="icon" />
            </button>
          </div>
          <div className={styles['page-guid']}>{page.guid}</div>
          <PageHeaderToggle page={page} />
        </div>
        <div
          className={classNames(styles['page-title'], {
            [styles['page-header-hidden']]: headerHidden,
          })}
        >
          <EditorInput
            name="title"
            value={page.title ?? ''}
            onChange={handleFieldChange}
            collaborator={collaboratorForTitle}
            placeholder="Add a page title"
          />
        </div>
        <PageTitleError title={page.title} />
        <div
          className={classNames(styles['page-description'], {
            [styles['page-header-hidden']]: headerHidden,
          })}
        >
          <EditorInput
            multiLine
            name="description"
            value={page.description || ''}
            onChange={handleFieldChange}
            collaborator={collaboratorForDescription}
            placeholder="Add a description"
          />
        </div>
        <section className={styles['page-elements']}>
          <label>Questions</label>
          <div>
            {elements.length === 0 && (
              <div className="invalid-feedback force-show">
                <ErrorMessage>At least one question is required</ErrorMessage>
              </div>
            )}
            {elements.map((e, i) => (
              <StandardElement key={e.guid} page={page} element={e} index={i} />
            ))}
          </div>
          <div>
            <Button color="primary" outline onClick={handleAddQuestion}>
              Add Question
            </Button>
          </div>
        </section>
        <section className={styles['page-footer']}>
          <div />
          <PageActions page={page} />
        </section>
      </div>
    </div>
  );
}

export default StandardQuestionsPage;
